import { useEffect, useMemo, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Image from '@tiptap/extension-image';
import { EntityLinkMark } from './EntityLinkMark';
import { EntityLinkPicker, type EntityLinkSelection } from './EntityLinkPicker';
import { ImagePickerModal, type ImageSelection } from './ImagePickerModal';
import type { EntityLinkType } from '../../services/entityLinks';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  readOnly?: boolean;
  minHeight?: number;
  /**
   * Caps the CONTENT area and scrolls inside it. Opt-in: without it the editor
   * grows with its text and the page scrolls, which is right for a short field
   * and wrong for a long-form body — an unbounded article body pushed the form's
   * Save button so far down that reaching it meant scrolling past the whole
   * article.
   *
   * The cap goes on the content, not on the container. The container must not
   * become a scroll box: the toolbar is `sticky` inside it, and a box that never
   * scrolls pins nothing (see the note above the container). Capping the
   * content instead leaves the toolbar sitting above the text as it scrolls.
   */
  maxHeight?: number | string;
  /**
   * Shows the expand control, and is called when it is pressed. The host owns
   * the expanded state — this component only asks for the toggle, because what
   * "full screen" means is the host's layout question, not the editor's.
   */
  onToggleExpand?: () => void;
  /** Which way the expand control should point. */
  expanded?: boolean;
  /**
   * Adds the entity-link button, which inserts in-app deep links to brands,
   * references, calibres and articles.
   *
   * Opt-in per surface rather than on everywhere, because the links are only
   * live where the RN client renders the body through the WebView renderer that
   * intercepts them. On a surface that renderer does not cover, an entity link
   * would look like a link and do nothing.
   */
  entityLinks?: boolean;
}

// Toolbar button helper
function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault(); // keep editor focus
        if (!disabled) onClick();
      }}
      title={title}
      disabled={disabled}
      className={`px-2 py-1 rounded text-sm transition-colors ${
        active
          ? 'bg-brand-600 text-white'
          : 'text-gray-700 hover:bg-gray-100'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-5 bg-gray-200 mx-0.5 self-center" />;
}

export function RichTextEditor({
  value,
  onChange,
  readOnly = false,
  minHeight = 400,
  maxHeight,
  onToggleExpand,
  expanded = false,
  entityLinks = false,
}: RichTextEditorProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        // heading levels 1-3
        heading: { levels: [1, 2, 3] },
        // disable strike from StarterKit so our extension takes over
        strike: false,
        // Link is configured explicitly below; leaving StarterKit's copy in too
        // registered the mark twice, which made its parse priority — and so
        // whether an entity anchor is claimed by Link — depend on load order.
        link: false,
        // Same duplicate registration, same fix — StarterKit v3 bundles this one
        // as well, and the file has always imported it separately.
        underline: false,
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer' } }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      // Registered on every surface, unconditionally, unlike the entity mark.
      // A node TipTap does not know is dropped when the document is parsed, so
      // while this was missing an `<img>` typed into the HTML source view — or
      // pasted in from anywhere — silently vanished the moment the editor was
      // switched back to visual mode. Base64 stays off: an inline data URI
      // would be written into the Firestore document itself.
      Image.configure({ inline: false, allowBase64: false }),
      ...(entityLinks ? [EntityLinkMark] : []),
    ],
    [entityLinks],
  );

  const editor = useEditor({
    extensions,
    content: value,
    editable: !readOnly,
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  // Sync external value changes (e.g. switching from HTML mode)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  // Sync readOnly changes
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [readOnly, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL', prev ?? '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const { from, to } = editor.state.selection;
  const selectedText = editor.state.doc.textBetween(from, to, ' ');
  // Only read the mark when it is registered: TipTap throws rather than
  // returning empty for a mark name the schema has never heard of, so an
  // unguarded read would take down every editor that opted out.
  const activeEntityType = entityLinks
    ? (editor.getAttributes('entityLink').entityType as
        | EntityLinkType
        | undefined)
    : undefined;

  const applyEntityLink = (selection: EntityLinkSelection) => {
    const attrs = {
      entityType: selection.type,
      entityId: selection.id,
      entityLabel: selection.label,
    };

    if (selectedText) {
      editor.chain().focus().extendMarkRange('entityLink').setEntityLink(attrs).run();
    } else {
      // Nothing selected, so the entity's own name becomes the link text —
      // otherwise the mark would be applied to an empty range and vanish.
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: selection.label,
          marks: [{ type: 'entityLink', attrs }],
        })
        .run();
    }
    setPickerOpen(false);
  };

  const removeEntityLink = () => {
    editor.chain().focus().unsetEntityLink().run();
    setPickerOpen(false);
  };

  // An image node is selected when the cursor is on it, which is what makes the
  // toolbar button an edit rather than a second insert.
  const activeImage = editor.isActive('image')
    ? {
        src: (editor.getAttributes('image').src as string | undefined) ?? '',
        alt: (editor.getAttributes('image').alt as string | undefined) ?? '',
      }
    : undefined;

  const applyImage = (selection: ImageSelection) => {
    // `alt` is omitted rather than written empty when the curator gave none, so
    // the editor does not emit an `alt=""` it was never told to.
    const attrs = selection.alt
      ? { src: selection.src, alt: selection.alt }
      : { src: selection.src };
    editor.chain().focus().setImage(attrs).run();
    setImagePickerOpen(false);
  };

  return (
    // No `overflow-hidden` here, deliberately: it would make this div a scroll
    // container, and the sticky toolbar would then pin inside a box that never
    // scrolls — visibly a no-op. The corner rounding it used to clip is on the
    // toolbar itself instead.
    <div className="border border-gray-300 rounded-lg">
      {!readOnly && (
        // Sticky so a long body keeps the controls reachable. `bg-gray-50` is
        // already opaque, so the text scrolls under it rather than through it.
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200 rounded-t-lg">
          {/* Headings */}
          <select
            value={
              editor.isActive('heading', { level: 1 }) ? '1'
              : editor.isActive('heading', { level: 2 }) ? '2'
              : editor.isActive('heading', { level: 3 }) ? '3'
              : '0'
            }
            onChange={(e) => {
              const level = parseInt(e.target.value);
              if (level === 0) {
                editor.chain().focus().setParagraph().run();
              } else {
                editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 }).run();
              }
            }}
            className="text-sm border border-gray-200 rounded px-1.5 py-0.5 bg-white text-gray-700 cursor-pointer"
          >
            <option value="0">Paragraph</option>
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
          </select>

          <Divider />

          {/* Inline formatting */}
          <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
            <em>I</em>
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
            <span className="underline">U</span>
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
            <span className="line-through">S</span>
          </ToolbarButton>

          <Divider />

          {/* Lists */}
          <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered list">
            1.
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">
            •
          </ToolbarButton>

          <Divider />

          {/* Alignment */}
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left">
            ≡L
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align center">
            ≡C
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right">
            ≡R
          </ToolbarButton>

          <Divider />

          {/* Link */}
          <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Insert/edit link">
            🔗
          </ToolbarButton>

          {entityLinks && (
            <ToolbarButton
              onClick={() => setPickerOpen(true)}
              active={editor.isActive('entityLink')}
              title="Link to a brand, reference, calibre or article"
            >
              🔗+ Entity
            </ToolbarButton>
          )}

          <Divider />

          {/* Image */}
          <ToolbarButton
            onClick={() => setImagePickerOpen(true)}
            active={editor.isActive('image')}
            title={
              activeImage
                ? 'Replace this image or edit its alt text'
                : 'Insert an image'
            }
          >
            🖼 Image
          </ToolbarButton>
          {activeImage && (
            <ToolbarButton
              onClick={() => editor.chain().focus().deleteSelection().run()}
              title="Remove this image"
            >
              ✕ Image
            </ToolbarButton>
          )}

          <Divider />

          {/* Table */}
          <ToolbarButton onClick={insertTable} title="Insert table">
            ⊞ Table
          </ToolbarButton>
          {editor.isActive('table') && (
            <>
              <ToolbarButton onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add column">
                +Col
              </ToolbarButton>
              <ToolbarButton onClick={() => editor.chain().focus().addRowAfter().run()} title="Add row">
                +Row
              </ToolbarButton>
              <ToolbarButton onClick={() => editor.chain().focus().deleteColumn().run()} title="Delete column">
                −Col
              </ToolbarButton>
              <ToolbarButton onClick={() => editor.chain().focus().deleteRow().run()} title="Delete row">
                −Row
              </ToolbarButton>
              <ToolbarButton onClick={() => editor.chain().focus().deleteTable().run()} title="Delete table">
                ✕ Table
              </ToolbarButton>
            </>
          )}

          <Divider />

          {/* Clear formatting */}
          <ToolbarButton onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Clear formatting">
            T̶
          </ToolbarButton>

          {/*
            Acts on the editor rather than on the text, so it sits apart from the
            formatting controls and at the far end of the row. Only rendered when
            the content is actually capped — an expand control on an editor that
            already shows everything is a button that does nothing visible.
          */}
          {onToggleExpand && maxHeight !== undefined && (
            <>
              <Divider />
              <div className="ml-auto">
                <ToolbarButton
                  onClick={onToggleExpand}
                  title={expanded ? 'Exit full screen' : 'Expand to full screen'}
                >
                  ⛶
                </ToolbarButton>
              </div>
            </>
          )}
        </div>
      )}
      <EditorContent
        editor={editor}
        className="rich-text-editor-content"
        style={{ minHeight, maxHeight, overflowY: maxHeight === undefined ? undefined : 'auto' }}
      />
      {entityLinks && (
        <EntityLinkPicker
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={applyEntityLink}
          initialType={activeEntityType}
          selectedText={selectedText}
          explainsLinkText
          onRemove={activeEntityType ? removeEntityLink : undefined}
        />
      )}
      <ImagePickerModal
        isOpen={imagePickerOpen}
        onClose={() => setImagePickerOpen(false)}
        onSelect={applyImage}
        initial={activeImage}
      />
    </div>
  );
}
